import React, { useState } from 'react';
import { Text, StyleSheet, Platform, StyleProp, TextStyle } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../styles/theme';
import {
  adjustQuantitiesInText,
  type AdjustOptions,
  type TextSegment,
} from '../../utils/adjustRecipeQuantities';

const isWeb = Platform.OS === 'web';

interface AdjustedTextProps {
  text: string;
  options: AdjustOptions;
  style?: StyleProp<TextStyle>;
  numberOfLines?: number;
}

/**
 * Renders step prose with every rescaled or converted measurement marked, and
 * every oven temperature completed with its twin scale.
 *
 * The two treatments are deliberately different, because they mean different
 * things: a *measure* was changed from what the author wrote (terracotta,
 * dotted underline, tap to see the original), a *temperature* was not changed
 * at all — only completed — so it stays in the body colour.
 */
export const AdjustedText: React.FC<AdjustedTextProps> = ({
  text,
  options,
  style,
  numberOfLines,
}) => {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState<number | null>(null);
  const segments = adjustQuantitiesInText(text, options);

  const originalLabel = (original: string) =>
    t('recipeDetail.originallyValue', {
      defaultValue: "à l'origine : {{value}}",
      value: original,
    });

  // Fast path: nothing to mark, so render one flat string.
  if (segments.length === 1 && segments[0].type === 'text') {
    return <Text style={style} numberOfLines={numberOfLines}>{text}</Text>;
  }

  return (
    <Text style={style} numberOfLines={numberOfLines}>
      {segments.map((segment, i) => renderSegment(segment, i, revealed, setRevealed, originalLabel))}
    </Text>
  );
};

function renderSegment(
  segment: TextSegment,
  index: number,
  revealed: number | null,
  setRevealed: (i: number | null) => void,
  originalLabel: (original: string) => string
) {
  if (segment.type === 'text') {
    return <Text key={index}>{segment.text}</Text>;
  }

  if (segment.type === 'temperature') {
    // Not a modification — the second reading is an addition, so it is only
    // de-emphasised, never marked.
    const [primary, ...rest] = segment.text.split(' (');
    return (
      <Text key={index}>
        {primary}
        <Text style={styles.altTemp}>{rest.length ? ` (${rest.join(' (')}` : ''}</Text>
      </Text>
    );
  }

  const isRevealed = revealed === index;
  return (
    <Text
      key={index}
      style={styles.mark}
      onPress={() => setRevealed(isRevealed ? null : index)}
      // Hover on web gives the original without a tap; the title attribute is
      // the native tooltip, which beats re-implementing one.
      {...(isWeb ? { title: originalLabel(segment.original) } : {})}
      accessibilityLabel={`${segment.text} — ${originalLabel(segment.original)}`}
    >
      {segment.text}
      {isRevealed ? <Text style={styles.reveal}>{` (${segment.original})`}</Text> : null}
    </Text>
  );
}

const styles = StyleSheet.create({
  mark: {
    color: Paprika.accent,
    ...(isWeb
      ? { textDecorationLine: 'underline', textDecorationStyle: 'dotted' as const }
      : {}),
  },
  reveal: {
    color: Paprika.textMuted,
    fontStyle: 'italic',
  },
  altTemp: {
    color: Paprika.textMuted,
  },
});

export default AdjustedText;
